import { InteractionType } from '@azure/msal-browser';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal, useMsalAuthentication } from '@azure/msal-react';
import { useAlert } from '@components/alerts';
import { Loading } from '@components/loading';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { aadConfig, logout, useSessionTimeout } from '@services/aad';
import { isExpectedSelfDirectedError } from '@services/msalInteraction';
import { needsName } from '@services/personName';
import useMe from '@services/state/account/hooks';
import { useAppSelector } from '@services/state/store';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { CompleteYourName } from './pages/common/CompleteYourName';
import { Layout } from './pages/layout/public';
import { OwnershipAssumed } from './pages/ownership/OwnershipAssumed';
import { OwnershipDecision } from './pages/ownership/OwnershipDecision';

const AccountClosed = React.lazy(() =>
  import('./pages/error/AccountClosed').then(({ AccountClosed }) => ({ default: AccountClosed })),
);
const AccountCanceling = React.lazy(() =>
  import('./pages/error/AccountCanceling').then(({ AccountCanceling }) => ({ default: AccountCanceling })),
);
const Login = React.lazy(() => import('./pages/Login').then(({ Login }) => ({ default: Login })));
const Logout = React.lazy(() => import('./pages/Logout').then(({ Logout }) => ({ default: Logout })));
const Dashboard = React.lazy(() =>
  import('./pages/dashboard/Dashboard').then(({ Dashboard }) => ({ default: Dashboard })),
);
const Onboarding = React.lazy(() =>
  import('./pages/onboarding/Onboarding').then(({ Onboarding }) => ({ default: Onboarding })),
);
const InviteVerify = React.lazy(() =>
  import('./pages/invite/Verify').then(({ InviteVerify }) => ({ default: InviteVerify })),
);
const InviteComplete = React.lazy(() =>
  import('./pages/invite/Complete').then(({ InviteComplete }) => ({ default: InviteComplete })),
);
const Register = React.lazy(() => import('./pages/Register').then(({ Register }) => ({ default: Register })));
const Signup = React.lazy(() => import('./pages/signup/Signup').then(({ Signup }) => ({ default: Signup })));
const StaffSignIn = React.lazy(() =>
  import('./pages/staff/StaffSignIn').then(({ StaffSignIn }) => ({ default: StaffSignIn })),
);
// const NotFound = React.lazy(() => import('./pages/error/NotFound').then(({ NotFound }) => ({ default: NotFound })));

const ProjectEntry = React.lazy(() =>
  import('./pages/projects/ProjectEntry').then(({ ProjectEntry }) => ({ default: ProjectEntry })),
);

const ProjectMatch = React.lazy(() =>
  import('./pages/projects/ProjectMatch').then(({ ProjectMatch }) => ({ default: ProjectMatch })),
);

/**
 * The bootstrap failed for a reason that is not a bad session.
 *
 * Shown rather than spun on. This screen exists because /users/me was returning 409 and the app
 * had nowhere to put that: the loader had no failure state, so a signed-in person sat watching a
 * spinner with no way to retry and nothing to tell support.
 *
 * A component of its own so the retry can keep using useMe(). It mounts only once meError is
 * already set, and useMe()'s effect returns immediately on that flag, so calling it here starts
 * nothing - whereas calling it at the root started a login during MSAL's redirect handling.
 */
const BootstrapFailed: React.FC = () => {
  const { t } = useTranslation();
  const [, refreshMe] = useMe();
  const detail = useAppSelector((state) => state.account.meErrorDetail);

  return (
    <Layout className="invite">
      <div className="error-code">
        <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
        {t('General.Errors.SignInTitle', 'We could not finish signing you in')}
      </div>
      <div className="error-text ui-text-center">
        {t(
          'General.Errors.SignIn',
          'Your session is fine, but we could not load your profile. Try again, and if it keeps happening tell support what this screen says.',
        )}
      </div>
      {detail && (
        <div className="ui-text-center ui-text-muted" style={{ fontSize: '0.85rem', marginTop: '0.75rem' }}>
          <code>{detail}</code>
        </div>
      )}
      <div className="ui-text-center ui-text-lg">
        <button type="button" className="ui-info" onClick={() => refreshMe()}>
          {t('General.TryAgain', 'Try again')}
        </button>
      </div>
    </Layout>
  );
};

/**
 * Will not let somebody past without a name.
 *
 * A one-time required step, not a prompt: a record with no name is why a proposal printed an
 * account handle where a person belonged and why a project manager showed as an email address, and
 * there is no honest way to fill one in except to ask. So there is no dismiss and no "later".
 *
 * Inside AuthenticatedTemplate on purpose. useMe() bootstraps, and at the root of the tree that is
 * fatal during the B2C round trip - see the comment in RoutesImpl. Down here MSAL is already done.
 *
 * Three paths stay open. The invite flow asks for the name itself, as part of accepting, so
 * gating it would ask twice and strand anybody whose acceptance has not run yet. Onboarding
 * collects a name on its own form. And logout is never blocked: a person who will not give a name
 * must still be able to leave.
 */
const NameRequired: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [me] = useMe();
  const location = useLocation();

  const exempt =
    location.pathname.startsWith('/invite') ||
    location.pathname.startsWith('/onboarding') ||
    location.pathname.startsWith('/logout') ||
    location.pathname.startsWith('/msal/');

  if (!exempt && needsName(me)) {
    return <CompleteYourName />;
  }

  return <>{children}</>;
};

const RoutesImpl: React.FC = () => {
  const { t } = useTranslation();

  /*
   * Read, not run.
   *
   * This used to be useMe(), and useMe() bootstraps: its effect calls refresh, refresh finds no
   * active account and calls login(), and login() clears the MSAL cache. At the top of the route
   * tree that is fatal, because hooks run before the msal.inProgress guard below can return
   * anything - so on the way back from B2C, while handleRedirectPromise was still resolving and
   * getAllAccounts() was still empty, the root threw away the code_verifier for the redirect it
   * was in the middle of and started another one. B2C signed the person in perfectly, five times
   * in five minutes, and the app never got far enough to ask it for a token.
   *
   * Every other useMe() lives below the guard, inside AuthenticatedTemplate, and mounts only once
   * MSAL is done. This one is now a plain selector, so the root observes the failure without
   * causing one.
   */
  const meError = useAppSelector((state) => !!state.account.meError);

  const msal = useMsal();
  const location = useLocation();

  const isInvite = location.pathname.startsWith('/invite');

  /*
   * Routes that ask B2C for themselves, so the root must not ask for them.
   *
   * `useMsalAuthentication` below starts a `prompt:'login'` redirect for every path that is not
   * one of these, and it starts it from hooks - before any route renders. For a page whose whole
   * job is to start a different interaction that is fatal twice over: the visitor is sent to
   * sign-in rather than where they were going, and if the page's own call lands as well, the
   * second caller throws and takes the first one's `code_verifier` with it.
   *
   * `/invite` has been exempt since invitees were being signed in as whoever B2C had in SSO.
   * `/signup` is the marketing site's role buttons, which were landing on sign-in for exactly the
   * reason above - the destination was behind the authenticated guard, so nothing but the root's
   * redirect ever ran. `/staff` is the same shape again: it starts its own interaction, carrying
   * the domain_hint that sends staff straight to the corporate directory.
   */
  const selfDirected = isInvite || location.pathname.startsWith('/signup') || location.pathname.startsWith('/staff');

  // Read once per render rather than in an effect: the redirect below has to win before the route
  // table resolves, otherwise onboarding routing gets there first and the invite is stranded.
  const pendingInviteKey = React.useMemo(() => {
    try {
      return localStorage.getItem('cc-invite-key') || undefined;
    } catch {
      return undefined;
    }
  }, [location.pathname]);
  const { error, result } = useMsalAuthentication(selfDirected ? InteractionType.None : InteractionType.Redirect, {
    scopes: aadConfig().scopes,
    redirectStartPage: location.pathname === '/' ? '/dashboard' : location.pathname,
    prompt: 'login',
    state: JSON.stringify({
      theme: localStorage.getItem('theme') || 'light',
      // The B2C custom UI reads this as stateEx.back - three cancel buttons across its
      // pages check it. It has been sent as "fallback" since it was introduced, which
      // matches nothing, so those buttons have always fallen through to history.back().
      //
      // Sent raw, not interpolated: a template literal turns an unset CC_SITE into the string
      // "undefined", which is truthy on the B2C side and would navigate to a relative path
      // called "undefined". Undefined drops out of JSON.stringify, so an unconfigured build
      // simply leaves the key absent and the cancel button keeps its history.back() behaviour.
      back: process.env.CC_SITE,
    }),
  });

  const { isNearEnd, isEnd } = useSessionTimeout();
  const { alertDanger, clearAlert } = useAlert();
  const [timeoutAlertId, setTimeoutAlertId] = React.useState(-1);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    window.dispatchEvent(new Event('toggleAlertDrawerClose'));
  }, [location]);

  React.useEffect(() => {
    if (!isNearEnd) {
      if (timeoutAlertId < 0) return;
      clearAlert(timeoutAlertId);
      setTimeoutAlertId(-1);
      return;
    }
    if (timeoutAlertId >= 0) return;

    setTimeout(() => {
      setTimeoutAlertId(
        alertDanger({
          title: t('General.TimeoutTitle'),
          message: t('General.Timeout'),
          noAutoHide: true,
        }),
      );
    });
  }, [isNearEnd, alertDanger, clearAlert, timeoutAlertId, t]);

  React.useEffect(() => {
    if (!isEnd) return;
    logout().then(() => {});
  }, [isEnd]);

  React.useEffect(() => {
    if (!error) return;

    /*
     * Two of the errors a self-directed route sees are the hook reporting it had nothing to do,
     * and signing somebody out over either of those is the loop this guard exists to prevent.
     *
     * Narrow to those two by code. Exempting the whole route - which is what the first version of
     * this did - also swallowed the real failures an authenticated visitor can hit through
     * `acquireTokenSilent`, and a server or network error there would have left the page sitting
     * with nothing said. Everything that is not one of the two reaches the same sign-out it would
     * reach on any other route.
     */
    if (selfDirected && isExpectedSelfDirectedError(error)) return;

    logout().then(() => {});
  }, [error, result, selfDirected]);

  if (['startup', 'handleRedirect'].includes(msal.inProgress)) return <Loading />;

  /*
   * The bootstrap failed for a reason that is not a bad session.
   *
   * Shown rather than spun on. This screen exists because /users/me was returning 409 and the app
   * had nowhere to put that: the loader had no failure state, so a signed-in person sat watching
   * a spinner with no way to retry and nothing to tell support. The retry is the same call, and
   * the sign-out is there for the case where it never comes back.
   */
  if (meError) {
    return <BootstrapFailed />;
  }

  return (
    <>
      <AuthenticatedTemplate>
        {/*
          An invitation that was verified but never completed.

          Verify stashes the key before handing off to B2C, and Complete reads it back. That
          fallback only ever ran if the B2C round trip returned the browser to /invite/complete,
          and it does not always: the return can be lost when the verification email is opened in
          a different tab from the one holding MSAL's redirect state. The invitee then arrives
          authenticated but unassociated, onboarding routes them to /onboarding/company, and the
          invitation sits Pending for ever.

          Redirecting here, before the route table is consulted, makes the existing fallback
          reachable from wherever the return lands. It is a redirect rather than a second
          completion call on purpose: Complete owns the completion, so every server guard - the
          wrong-principal email match, and the seat check - applies exactly once, in one place.
        */}
        {pendingInviteKey && !isInvite ? (
          <Navigate to={`/invite/complete?key=${encodeURIComponent(pendingInviteKey)}`} replace />
        ) : (
          <NameRequired>
            <Routes>
              <Route path="/msal/callback" element={<div />} />

              {/*
            Setting up the firm's account. Reachable by a project-scoped guest, which is the point:
            it is how they stop being one. The server authorises the act itself.
          */}
              <Route path="/ownership" element={<OwnershipDecision />} />
              <Route path="/ownership/assumed" element={<OwnershipAssumed />} />
              <Route path="/approval-closed" element={<AccountClosed />} />
              <Route path="/account-canceling" element={<AccountCanceling />} />
              <Route path="/dashboard/:accountId?/*" element={<Dashboard />} />
              <Route path="/invite/*">
                <Route index element={<InviteVerify />} />
                <Route path="complete" element={<InviteComplete />} />
              </Route>
              <Route path="/login" element={<Login />} />
              <Route path="/logout" element={<Logout />} />
              <Route path="projects/*">
                <Route index element={<ProjectEntry />} />
                <Route path=":projectId/:path*" element={<ProjectMatch />} />
              </Route>
              <Route path="/register/:which?" element={<Register />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/staff" element={<StaffSignIn />} />
              <Route path="/onboarding/*" element={<Onboarding />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </NameRequired>
        )}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Routes>
          <Route path="/msal/callback" element={<div />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/invite/*">
            <Route index element={<InviteVerify />} />
            <Route path="complete" element={<InviteComplete />} />
          </Route>
          {/* A role button on the marketing site. Public on purpose - see Signup.tsx. */}
          <Route path="/signup" element={<Signup />} />
          {/* The staff door. Public on purpose - see StaffSignIn.tsx. */}
          <Route path="/staff" element={<StaffSignIn />} />
          <Route path="*" element={<Loading />} />
        </Routes>
      </UnauthenticatedTemplate>
    </>
  );
};
export default RoutesImpl;
